import React from "react";
import {
    Box,
    Typography,
    Switch,
    Tooltip,
    Table,
    TableHead,
    TableBody,
    TableCell,
    TableRow,
    ButtonGroup,
    IconButton,
    Chip,
} from "@mui/material";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

// Components
import Icon from "@/theme/vectors/bundle";
import { TextField } from "@/theme/inputs/textfields";

// Utils
import { formatVariantName, formatPrice, getMediaTypeIcon } from "@/utils/products/variantHelpers";
// Alternatively, import getMediaTypeIcon directly from mediaHelpers if preferred
// import { getMediaTypeIcon } from "@/utils/products/mediaHelpers";

const ProductVariants = ({
    variants,
    medias,
    onPriceCellClick,
    onPriceChange,
    onPriceUpdate,
    onPriceKeyPress,
    onPrediscountPriceCellClick,
    onPrediscountPriceChange,
    onPrediscountPriceUpdate,
    onPrediscountPriceKeyPress,
    onActiveChange,
    onLockChange,
    onMediaOrderChange,
    onRemoveMediaFromVariant,
    onSetMainMedia,
    tempPriceValue,
    editingPriceVariantId,
    tempPrediscountPriceValue,
    editingPrediscountPriceVariantId,
    setActiveVariantId,
    openMediaPanel,
    updateVariantMediaOrder,  // New prop for API update function
    t
}) => {
    // Modified to work with both structures
    const getMediaById = (variant, mediaId) => {
        // If the variant has a media array (API structure)
        if (variant.media && Array.isArray(variant.media)) {
            return variant.media.find(media => media.id === mediaId);
        }
        // Fall back to old structure if medias prop is available
        return medias?.find(media => media.id === mediaId);
    };

    // Helper to get the URL from a media object based on API structure
    const getMediaUrl = (media) => {
        return media?.media_url || media?.url || '';
    };

    // Helper to get the media type from a media object based on API structure
    const getMediaType = (media) => {
        return media?.media_type || media?.type || '';
    };

    // Check if variant has media (works with both structures)
    const hasMedia = (variant) => {
        return (variant.media && variant.media.length > 0) || (variant.mediaIds && variant.mediaIds.length > 0);
    };

    // Get media array from variant (works with both structures)
    const getVariantMedia = (variant) => {
        // If using API structure with nested media array
        if (variant.media && Array.isArray(variant.media)) {
            return variant.media;
        }
        // If using old structure with mediaIds and separate medias prop
        if (variant.mediaIds && Array.isArray(variant.mediaIds) && medias) {
            return variant.mediaIds.map(id => medias.find(m => m.id === id)).filter(Boolean);
        }
        return [];
    };

    // Handle drag end event for media reordering
    const handleMediaDragEnd = (variantId, result) => {
        if (!result.destination) return; // Dropped outside the list
        
        const sourceIndex = result.source.index;
        const destinationIndex = result.destination.index;
        
        if (sourceIndex === destinationIndex) return; // No change in position
        
        // Find the variant
        const variant = variants.find(v => v.id === variantId);
        if (!variant) return;
        
        // Get media array
        const variantMedia = getVariantMedia(variant);
        
        // Create a new array with the reordered items
        const reorderedMedia = Array.from(variantMedia);
        const [movedItem] = reorderedMedia.splice(sourceIndex, 1);
        reorderedMedia.splice(destinationIndex, 0, movedItem);
        
        // First, call onMediaOrderChange to update local state (UI)
        if (onMediaOrderChange) {
            onMediaOrderChange(variantId, result);
        }
        
        // Then, prepare data for API update
        const mediaSortOrders = reorderedMedia.map((media, index) => {
            const mediaId = typeof media === 'string' ? media : media.id;
            return {
                mediaId: mediaId,
                sortOrder: index + 1  // 1-based sort order
            };
        });
        
        // Call API update function
        if (updateVariantMediaOrder) {
            const payload = {
                variantId: variantId,
                mediaSortOrders: mediaSortOrders
            };
            
            updateVariantMediaOrder(payload);
        }
    };

    return (
        <Table size="small">
            <TableHead>
                <TableRow>
                    <TableCell>{t('product.detail.variantColumn')}</TableCell>
                    <TableCell>{t('product.detail.mediaColumn')}</TableCell>
                    <TableCell align="right">{t('product.detail.priceColumn')}</TableCell>
                    <TableCell align="right">{t('product.detail.prediscountPrice')}</TableCell>
                    <TableCell align="center">{t('product.detail.statusColumn')}</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {variants.map((variant) => (
                    <TableRow key={variant.id}>
                        <TableCell>
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                <Typography variant="subtitle2">
                                    {formatVariantName(variant)}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                    {variant.id}
                                </Typography>
                            </Box>
                        </TableCell>
                        <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {hasMedia(variant) ? (
                                    <DragDropContext 
                                        onDragEnd={(result) => handleMediaDragEnd(variant.id, result)}
                                    >
                                        <Droppable droppableId={`droppable-${variant.id}`} direction="horizontal">
                                            {(provided) => (
                                                <Box
                                                    ref={provided.innerRef}
                                                    {...provided.droppableProps}
                                                    sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}
                                                >
                                                    {getVariantMedia(variant).map((media, index) => {
                                                        // If we're using the old structure, media might be an ID
                                                        const mediaId = typeof media === 'string' ? media : media.id;
                                                        // If media is an ID, fetch the full object
                                                        const mediaObj = typeof media === 'string' ? getMediaById(variant, mediaId) : media;
                                                        
                                                        return (
                                                            <Draggable 
                                                                key={mediaId} 
                                                                draggableId={mediaId} 
                                                                index={index}
                                                            >
                                                                {(provided) => (
                                                                    <Box
                                                                        ref={provided.innerRef}
                                                                        {...provided.draggableProps}
                                                                        {...provided.dragHandleProps}
                                                                        sx={{ position: 'relative' }}
                                                                    >
                                                                        <Tooltip title={mediaObj?.filename || "Media"}>
                                                                            <Box sx={{ 
                                                                                width: 40, 
                                                                                height: 40, 
                                                                                border: '2px solid',
                                                                                borderColor: variant.mediaId === mediaId ? 'primary.main' : 'divider',
                                                                                borderRadius: 1,
                                                                                overflow: 'hidden',
                                                                                cursor: 'pointer',
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'center',
                                                                                backgroundColor: '#f5f5f5',
                                                                                '&:hover': {
                                                                                    opacity: 0.8,
                                                                                    '& .media-actions': {
                                                                                        opacity: 1
                                                                                    }
                                                                                }
                                                                            }}>
                                                                                {getMediaType(mediaObj) === 'image' ? (
                                                                                    <img 
                                                                                        src={getMediaUrl(mediaObj)} 
                                                                                        alt="Variant media" 
                                                                                        style={{ 
                                                                                            width: '100%', 
                                                                                            height: '100%', 
                                                                                            objectFit: 'cover' 
                                                                                        }} 
                                                                                        onClick={() => onSetMainMedia(variant.id, mediaId)}
                                                                                    />
                                                                                ) : (
                                                                                    <Box
                                                                                        onClick={() => onSetMainMedia(variant.id, mediaId)}
                                                                                        sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                                                    >
                                                                                        {getMediaTypeIcon(getMediaType(mediaObj))}
                                                                                    </Box>
                                                                                )}
                                                                                <Box 
                                                                                    className="media-actions"
                                                                                    sx={{ 
                                                                                        position: 'absolute', 
                                                                                        top: 0, 
                                                                                        right: 0, 
                                                                                        opacity: 0,
                                                                                        transition: 'opacity 0.2s'
                                                                                    }}
                                                                                >
                                                                                    <IconButton
                                                                                        size="small"
                                                                                        sx={{
                                                                                            bgcolor: 'background.paper',
                                                                                            border: '1px solid',
                                                                                            borderColor: 'divider',
                                                                                            p: 0.2,
                                                                                            width: 18,
                                                                                            height: 18
                                                                                        }}
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            onRemoveMediaFromVariant(variant.id, mediaId);
                                                                                        }}
                                                                                    >
                                                                                        <Icon name="remove" size={12} />
                                                                                    </IconButton>
                                                                                </Box>
                                                                            </Box>
                                                                        </Tooltip>
                                                                    </Box>
                                                                )}
                                                            </Draggable>
                                                        );
                                                    })}
                                                    {provided.placeholder}
                                                </Box>
                                            )}
                                        </Droppable>
                                    </DragDropContext>
                                ) : (
                                    <Tooltip title={t('product.detail.addMediaToVariant')}>
                                        <Box 
                                            sx={{ 
                                                width: 40, 
                                                height: 40, 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                justifyContent: 'center',
                                                border: '1px dashed',
                                                borderColor: 'divider',
                                                borderRadius: 1,
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    borderColor: 'primary.main',
                                                    color: 'primary.main'
                                                }
                                            }}
                                            onClick={() => {
                                                setActiveVariantId(variant.id);
                                                openMediaPanel();
                                            }}
                                        >
                                            <Icon name="add" size={20} />
                                        </Box>
                                    </Tooltip>
                                )}
                                {hasMedia(variant) && (
                                    <Tooltip title={t('product.detail.addMoreMedia')}>
                                        <Box 
                                            sx={{ 
                                                width: 40, 
                                                height: 40, 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                justifyContent: 'center',
                                                border: '1px dashed',
                                                borderColor: 'divider',
                                                borderRadius: 1,
                                                cursor: 'pointer',
                                                ml: 1,
                                                '&:hover': {
                                                    borderColor: 'primary.main',
                                                    color: 'primary.main'
                                                }
                                            }}
                                            onClick={() => {
                                                setActiveVariantId(variant.id);
                                                openMediaPanel();
                                            }}
                                        >
                                            <Icon name="add" size={20} />
                                        </Box>
                                    </Tooltip>
                                )}
                            </Box>
                        </TableCell>
                        <TableCell align="right">
                            {editingPriceVariantId === variant.id ? (
                                <TextField
                                    size="small"
                                    value={tempPriceValue}
                                    onChange={onPriceChange}
                                    onBlur={() => onPriceUpdate(variant.id)}
                                    onKeyDown={(e) => onPriceKeyPress(e, variant.id)}
                                    autoFocus
                                    variant="outlined"
                                    disabled={variant.is_locked}
                                    placeholder="--"
                                    sx={{ 
                                        width: 120,
                                        '& input': {
                                            textAlign: 'right',
                                        }
                                    }}
                                    InputProps={{
                                        inputProps: {
                                            style: { textAlign: 'right' }
                                        }
                                    }}
                                />
                            ) : (
                                <Box 
                                    sx={{ 
                                        cursor: variant.is_locked ? 'default' : 'pointer',
                                        p: 1,
                                        minWidth: 80,
                                        textAlign: 'right',
                                        '&:hover': {
                                            bgcolor: variant.is_locked ? 'transparent' : 'action.hover',
                                        }
                                    }}
                                    onClick={() => {
                                        if (!variant.is_locked) {
                                            onPriceCellClick(variant);
                                        }
                                    }}
                                >
                                    {variant.price ? (
                                        <Typography>
                                            {formatPrice(variant.price)}
                                        </Typography>
                                    ) : (
                                        <Typography color="textSecondary" variant="body2">
                                            {variant.is_locked ? 'N/A' : '--'}
                                        </Typography>
                                    )}
                                </Box>
                            )}
                        </TableCell>
                        <TableCell align="right">
                            {editingPrediscountPriceVariantId === variant.id ? (
                                <TextField
                                    size="small"
                                    value={tempPrediscountPriceValue}
                                    onChange={onPrediscountPriceChange}
                                    onBlur={() => onPrediscountPriceUpdate(variant.id)}
                                    onKeyDown={(e) => onPrediscountPriceKeyPress(e, variant.id)}
                                    autoFocus
                                    variant="outlined"
                                    disabled={variant.is_locked}
                                    placeholder="--"
                                    sx={{ 
                                        width: 120,
                                        '& input': {
                                            textAlign: 'right',
                                        }
                                    }}
                                    InputProps={{
                                        inputProps: {
                                            style: { textAlign: 'right' }
                                        }
                                    }}
                                />
                            ) : (
                                <Box 
                                    sx={{ 
                                        cursor: variant.is_locked ? 'default' : 'pointer',
                                        p: 1,
                                        minWidth: 80,
                                        textAlign: 'right',
                                        '&:hover': {
                                            bgcolor: variant.is_locked ? 'transparent' : 'action.hover',
                                        }
                                    }}
                                    onClick={() => {
                                        if (!variant.is_locked) {
                                            onPrediscountPriceCellClick(variant);
                                        }
                                    }}
                                >
                                    {variant.prediscount_price ? (
                                        <Typography>
                                            {formatPrice(variant.prediscount_price)}
                                        </Typography>
                                    ) : (
                                        <Typography color="textSecondary" variant="body2">
                                            {variant.is_locked ? 'N/A' : '--'}
                                        </Typography>
                                    )}
                                </Box>
                            )}
                        </TableCell>
                        <TableCell align="center">
                            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                                <Tooltip title={variant.is_active ? t('product.detail.variantActive') : t('product.detail.variantInactive')}>
                                    <Box>
                                        <Switch
                                            size="small"
                                            checked={variant.is_active}
                                            onChange={(e) => onActiveChange(variant.id, e.target.checked)}
                                            disabled={variant.is_locked}
                                            color="success"
                                        />
                                        <Typography variant="caption" display="block" textAlign="center">
                                            {t('product.detail.variantActive')}
                                        </Typography>
                                    </Box>
                                </Tooltip>
                                
                                <Tooltip title={variant.is_locked ? t('product.detail.variantLocked') : t('product.detail.variantUnlocked')}>
                                    <Box>
                                        <Switch
                                            size="small"
                                            checked={variant.is_locked}
                                            onChange={(e) => onLockChange(variant.id, e.target.checked)}
                                            color="error"
                                            sx={{
                                                '& .MuiSwitch-switchBase.Mui-checked': {
                                                    color: '#ff0000',
                                                },
                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                    backgroundColor: '#ff0000',
                                                },
                                            }}
                                        />
                                        <Typography variant="caption" display="block" textAlign="center">
                                            {t('product.detail.variantLock')}
                                        </Typography>
                                    </Box>
                                </Tooltip>
                            </Box>
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
};

export default ProductVariants;